import React, { useMemo } from 'react';
import BrandLogo from '@components/BrandLogo';
import './AppLoadingScreen.scss';

export type AppLoadingScreenVariant = 'fullscreen' | 'overlay' | 'inline';
export type AppLoadingScreenContext = 'auth' | 'route' | 'page' | 'data';

const CONTEXT_COPY: Record<
  AppLoadingScreenContext,
  { message: string; submessage: string }
> = {
  auth: {
    message: 'Verifying access',
    submessage: 'Checking your session…',
  },
  route: {
    message: 'Loading page',
    submessage: 'Preparing your workspace…',
  },
  page: {
    message: 'Please wait',
    submessage: 'This will only take a moment…',
  },
  data: {
    message: 'Loading data',
    submessage: 'Fetching the latest information…',
  },
};

export type AppLoadingScreenProps = {
  variant?: AppLoadingScreenVariant;
  context?: AppLoadingScreenContext;
  message?: string;
  submessage?: string;
  className?: string;
};

const AppLoadingScreen: React.FC<AppLoadingScreenProps> = ({
  variant = 'fullscreen',
  context = 'page',
  message,
  submessage,
  className = '',
}) => {
  const copy = CONTEXT_COPY[context];
  const title = message ?? copy.message;
  const subtitle = submessage ?? copy.submessage;

  const rootClass = useMemo(
    () =>
      ['app-loading-screen', `app-loading-screen--${variant}`, className]
        .filter(Boolean)
        .join(' '),
    [variant, className]
  );

  return (
    <div
      className={rootClass}
      role="status"
      aria-live="polite"
      aria-busy="true"
      aria-label={title}
    >
      <div className="app-loading-screen__backdrop" aria-hidden="true">
        <span className="app-loading-screen__orb app-loading-screen__orb--1" />
        <span className="app-loading-screen__orb app-loading-screen__orb--2" />
        <span className="app-loading-screen__orb app-loading-screen__orb--3" />
      </div>

      <div className="app-loading-screen__panel">
        <div className="app-loading-screen__logo-wrap">
          <BrandLogo width={52} className="app-loading-screen__logo" />
          <span className="app-loading-screen__logo-ring" aria-hidden="true" />
        </div>

        <div className="app-loading-screen__copy">
          <h2 className="app-loading-screen__title">{title}</h2>
          <p className="app-loading-screen__subtitle">{subtitle}</p>
        </div>

        <div className="app-loading-screen__progress" aria-hidden="true">
          <span className="app-loading-screen__progress-bar" />
        </div>

        <div className="app-loading-screen__skeleton" aria-hidden="true">
          <span />
          <span />
          <span />
        </div>
      </div>
    </div>
  );
};

export default AppLoadingScreen;
